import { useId } from "react";

import { Button } from "../../../../../core/Button.js";
import { EmptyState } from "../../../../../core/EmptyState.js";
import { Skeleton } from "../../../../../core/Skeleton.js";
import { Text } from "../../../../../core/Text.js";
import { BattleCard } from "../../../../../domain/battles/BattleCard.js";
import { LoadError } from "../../../../shared/LoadError.js";
import type { BattlesListContent } from "../../../battlesContent.js";

const GRID = "grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3";

/**
 * The battles, grouped (open lobbies, then in progress, then finished), as cards three across
 * from `xl`; or the loading, failed or empty state in their place. More of what is loaded, and
 * the next page, come on request.
 */
export function BattlesList({ label, state, more, loadMore }: BattlesListContent) {
  const id = useId();
  return (
    <section
      aria-label={label}
      aria-busy={state.status === "loading" || undefined}
      className="flex flex-col gap-8"
    >
      {state.status === "loading" && (
        <div className={GRID}>
          {["a", "b", "c"].map((key) => (
            <Skeleton key={key} className="h-48 rounded-2xl" />
          ))}
        </div>
      )}
      {state.status === "error" && <LoadError message={state.message} />}
      {state.status === "empty" && <EmptyState title={state.message} />}
      {state.status === "ready" &&
        state.groups.map((group) => (
          <section
            key={group.key}
            aria-labelledby={`${id}-${group.key}`}
            className="flex flex-col gap-3"
          >
            <Text as="h2" id={`${id}-${group.key}`} variant="caption-eyebrow" tone="tertiary">
              {group.title}
            </Text>
            <ul className={GRID}>
              {group.cards.map(({ id: cardId, ...card }) => (
                <li key={cardId} className="flex min-w-0">
                  <BattleCard {...card} className="flex-1" />
                </li>
              ))}
            </ul>
          </section>
        ))}
      {(more || loadMore) && (
        <div className="flex flex-wrap justify-center gap-3">
          {more && (
            <Button variant="secondary" onClick={more.onMore}>
              {more.label}
            </Button>
          )}
          {loadMore && (
            <Button variant="secondary" loading={loadMore.loading} onClick={loadMore.onLoadMore}>
              {loadMore.label}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
